import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Client-side defense-in-depth for URL canonicalization.
 *
 * The authoritative 301 redirects live at the edge (public/_redirects and
 * vercel.json). This component only fires when the edge layer hasn't been
 * deployed yet (e.g. the historical preview, local dev), so users never land on
 * a duplicate URL and every path resolves to exactly one canonical route.
 *
 * Keep this list synchronized with public/_redirects.
 */

const EXACT_REDIRECTS: Record<string, string> = {
  // Service canonical dedup
  '/electrical-panel-upgrade': '/services/panel-upgrades',
  '/generator-installations': '/services/generators',
  '/services/generator-installation': '/services/generators',
  '/services/generator-installion': '/services/generators',
  '/new-home': '/services/new-construction',
  '/wire-commercial-build-out': '/services/commercial',
  '/services/ev-charging-stations': '/services/ev-charging',
  '/installation-of-appliance': '/services/appliance-installation',
  '/repairs-and-maintenance': '/services/repairs-and-maintenance',
  '/designer-lighting': '/services/lighting',
  '/safety-inspections': '/services/safety-inspections',
  '/saftey-inspections': '/services/safety-inspections',

  // Location slug canonicalization
  '/locations/deleon-springs': '/locations/de-leon-springs',

  // Other legacy aliases
  '/contact-us': '/contact',
  '/about-us': '/about',
  '/services/smart-home': '/services',
  '/services/smart-home-integration': '/services',
  '/electrical-services': '/services',
};

// Prefix rewrites: /cities/* -> /locations/*
const PREFIX_REDIRECTS: Array<{ from: string; to: string }> = [
  { from: '/cities/', to: '/locations/' },
];

const RedirectHandler = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // URL-encoded spaces in slugs ("/locations/new%20smyrna%20beach")
    if (location.pathname.includes('%20')) {
      const decoded = decodeURIComponent(location.pathname);
      const corrected = decoded.replace(/\s+/g, '-').toLowerCase();
      if (corrected !== location.pathname) {
        navigate(corrected + location.search + location.hash, { replace: true });
        return;
      }
    }

    // Exact match
    const exact = EXACT_REDIRECTS[location.pathname];
    if (exact) {
      navigate(exact + location.search + location.hash, { replace: true });
      return;
    }

    // /cities -> /locations (exact and prefix)
    if (location.pathname === '/cities') {
      navigate('/locations' + location.search + location.hash, { replace: true });
      return;
    }
    for (const { from, to } of PREFIX_REDIRECTS) {
      if (location.pathname.startsWith(from)) {
        const tail = location.pathname.slice(from.length);
        navigate(to + tail + location.search + location.hash, { replace: true });
        return;
      }
    }
  }, [location.pathname, location.search, location.hash, navigate]);

  return null;
};

export default RedirectHandler;
